import { useEffect, useMemo, useState } from "react";
import { Card, Grid, Popover, ToggleButton, ToggleButtonGroup } from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import useCommon from "hooks/useCommon";
import Snackbar from "components/Snackbar";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import HotelSelector from "components/HotelSelector";

import {
  createMaintenanceTask,
  getMaintenanceTasks,
  getMaintenanceCounters,
  resetCreateTaskState,
} from "store/slice/maintenanceSlice";

import { FaCalendarAlt, FaChevronDown, FaList, FaPlus, FaThLarge } from "react-icons/fa";
import {
  FaTools,
  FaExclamationTriangle,
  FaCalendarCheck,
  FaDollarSign,
  FaWallet,
} from "react-icons/fa";

import MaintenanceCard from "./components/MaintenanceCard";
import MaintenanceStats from "./components/MaintenanceStats";
import KpiCard from "components/KpiCard";
import MaintenanceTaskDialog from "./components/MaintenanceTaskDialog";
import MaintenanceBudgetDialog from "./components/MaintenanceBudgetDialog";
import MaintenanceDetailDialog from "./components/MaintenanceDetailDialog";
import MaintenanceTable from "./components/MaintenanceTable";
import VuiAutocomplete from "components/AutoComplete";
import dayjs from "dayjs";
import MonthSelector from "components/MonthSelector";
import { updateMaintenanceBudget } from "store/slice/maintenanceSlice";
import CardSkeleton from "components/Skeletons/CardSkeleton";
import {
  getMaintenanceTaskById,
  updateMaintenanceTask,
  resetUpdateTaskState,
  deleteMaintenanceTask,
  resetDeleteTaskState,
} from "store/slice/maintenanceSlice";
import DeleteMaintenanceTaskDialog from "./components/DeleteMaintenanceTaskDialog";
import usePermissions from "hooks/usePermissions";
import colors from "assets/theme/base/colors";

function MaintenanceHub() {
  const { DISPATCH, STORE } = useCommon();
  const { can } = usePermissions();
  const { primary, dark, light } = colors;

  const {
    tasks,
    maintenanceCounters,
    tasksLoading,
    tasksError,
    pagination,

    createTaskLoading,
    createTaskError,
    createTaskSuccess,

    updateBudgetLoading,
    updateBudgetError,
    updateBudgetSuccess,

    selectedTask,
    selectedTaskLoading,
    selectedTaskError,

    updateTaskLoading,
    updateTaskError,
    updateTaskSuccess,
    updateTaskStatusSuccess,

    deleteTaskLoading,
    deleteTaskError,
    deleteTaskSuccess,
  } = STORE((state) => state.maintenance);

  const hasMore = pagination?.hasNextPage;

  const hotelIds = STORE((state) => state.ui.hotelIds);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [viewMode, setViewMode] = useState("card");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [monthFilter, setMonthFilter] = useState(dayjs().format("YYYY-MM"));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    show: false,
    color: "success",
    title: "",
    content: "",
    icon: "check",
  });

  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);

  const showSnackbar = (data) =>
    setSnackbar({
      show: true,
      ...data,
    });

  const closeSnackbar = () =>
    setSnackbar((prev) => ({
      ...prev,
      show: false,
    }));

  const formatStatusForAPI = (status) => {
    if (status === "In Progress" || status === "in_progress") return "InProgress";
    return status;
  };

  const loadMore = async () => {
    if (loadingMore || tasksLoading || !pagination?.hasNextPage) {
      return;
    }

    setLoadingMore(true);

    const nextPage = page + 1;

    try {
      const res = await DISPATCH(
        getMaintenanceTasks({
          hotelId: hotelIds[0],
          monthYear: monthFilter ? dayjs(monthFilter).format("MM/YYYY") : dayjs().format("MM/YYYY"),
          status: statusFilter === "All" ? undefined : statusFilter.replace(" ", ""),
          search: debouncedSearch || undefined,
          page: nextPage,
          limit: 10,
        })
      ).unwrap();

      if (res?.data?.tasks?.length > 0) {
        setPage(nextPage);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSaveBudget = async ({ budget, month }) => {
    try {
      await DISPATCH(
        updateMaintenanceBudget({
          hotelId: hotelIds?.[0],
          monthYear: dayjs(month).format("MM/YYYY"),
          amount: Number(budget),
        })
      ).unwrap();

      showSnackbar({
        color: "success",
        title: "Budget Updated",
        content: `Budget updated for ${dayjs(month).format("MMMM YYYY")}`,
        icon: "check",
      });

      setBudgetDialogOpen(false);

      DISPATCH(
        getMaintenanceCounters({
          hotelId: hotelIds?.[0],
          monthYear: monthFilter ? dayjs(monthFilter).format("MM/YYYY") : dayjs().format("MM/YYYY"),
        })
      );
    } catch (error) {
      // showSnackbar({
      //   color: "error",
      //   title: "Error",
      //   content: typeof error === "string" ? error : error?.message || "Something went wrong",
      //   icon: "warning",
      // });
    }
  };

  const handleCreateMaintenanceTask = async (payload) => {
    const requestBody = {
      hotelId: hotelIds?.[0],
      taskTitle: payload.title,
      description: payload.description,
      category: payload.categoryLabel,
      roomArea: payload.roomArea,
      priority: payload.priorityLabel,
      status: formatStatusForAPI(payload.statusLabel),
      assignedStaffIds: payload.assignedToUserIds || null,
      estimatedRepairCost: Number(payload.estimatedCost || 0),
      scheduledDate: payload.scheduledDate ? new Date(payload.scheduledDate).toISOString() : null,
      lastKnownUpdatedAt: new Date().toISOString(),
    };

    try {
      const res = await DISPATCH(createMaintenanceTask(requestBody)).unwrap();

      showSnackbar({
        color: "success",
        title: "Task Created",
        content: res?.message || "Maintenance task created successfully",
        icon: "check",
      });

      DISPATCH(resetCreateTaskState());
      setTaskDialogOpen(false);
    } catch (err) {
      // showSnackbar({
      //   color: "error",
      //   title: "Error",
      //   content: err || "Failed to create maintenance task",
      //   icon: "warning",
      // });
    }
  };

  const handleEditTask = async (task) => {
    try {
      setDialogMode("edit");

      setTaskDialogOpen(true);

      await DISPATCH(getMaintenanceTaskById(task.id));
    } catch (error) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: error || "Failed to load task details",
        icon: "warning",
      });
    }
  };

  const handleViewTask = async (task) => {
    setDetailOpen(true);
    try {
      await DISPATCH(getMaintenanceTaskById(task.id)).unwrap();
    } catch (error) {
      setDetailOpen(false);

      showSnackbar({
        color: "error",
        title: "Error",
        content:
          typeof error === "string" ? error : error?.message || "Failed to load task details",
        icon: "warning",
      });
    }
  };

  const handleUpdateTask = async (payload) => {
    try {
      await DISPATCH(
        updateMaintenanceTask({
          taskId: selectedTask.id,
          taskTitle: payload.title,
          description: payload.description,
          category: payload.categoryLabel,
          roomArea: payload.roomArea,
          priority: payload.priorityLabel,
          status: formatStatusForAPI(payload.statusLabel),
          assignedStaffIds: payload.assignedToUserIds || null,
          estimatedRepairCost: Number(payload.estimatedCost || 0),
          scheduledDate: payload.scheduledDate,
          lastKnownUpdatedAt: selectedTask.updatedAt,
        })
      ).unwrap();

      showSnackbar({
        color: "success",
        title: "Task Updated",
        content: "Maintenance task updated successfully",
        icon: "check",
      });

      setTaskDialogOpen(false);
    } catch (error) {
      showSnackbar({
        color: "error",
        title: "Error",
        content: error || "Failed to update task",
        icon: "warning",
      });
    }
  };

  const handleDelete = (task) => {
    DISPATCH(resetDeleteTaskState());

    setTaskToDelete(task);

    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteTaskLoading) return;

    DISPATCH(resetDeleteTaskState());
    setTaskToDelete(null);
    setDeleteDialogOpen(false);
  };

  const handleConfirmDeleteTask = async () => {
    if (!taskToDelete?.id) return;

    try {
      const res = await DISPATCH(deleteMaintenanceTask(taskToDelete.id)).unwrap();

      showSnackbar({
        color: "success",
        title: "Task Deleted",
        content: res?.apiRes?.message || "Maintenance task deleted successfully",
        icon: "check",
      });

      DISPATCH(resetDeleteTaskState());
      setTaskToDelete(null);
      setDeleteDialogOpen(false);
    } catch (error) {}
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const value = search.trim();

      if (value.length === 0 || value.length > 3) {
        setDebouncedSearch(value);
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!hotelIds?.[0]) return;

    DISPATCH(
      getMaintenanceTasks({
        hotelId: hotelIds[0],
        monthYear: monthFilter ? dayjs(monthFilter).format("MM/YYYY") : dayjs().format("MM/YYYY"),
        status: statusFilter === "All" ? undefined : statusFilter.replace(" ", ""),
        search: debouncedSearch || undefined,
        page,
        limit: rowsPerPage,
      })
    );
  }, [
    hotelIds,
    monthFilter,
    statusFilter,
    debouncedSearch,
    page,
    rowsPerPage,
    DISPATCH,
    updateTaskSuccess,
    createTaskSuccess,
    updateTaskStatusSuccess,
    deleteTaskSuccess,
  ]);

  useEffect(() => {
    if (!hotelIds?.[0]) return;

    DISPATCH(
      getMaintenanceCounters({
        hotelId: hotelIds[0],
        monthYear: monthFilter ? dayjs(monthFilter).format("MM/YYYY") : dayjs().format("MM/YYYY"),
      })
    );
  }, [
    hotelIds,
    monthFilter,
    DISPATCH,
    updateBudgetSuccess,
    updateTaskSuccess,
    createTaskSuccess,
    updateTaskStatusSuccess,
    deleteTaskSuccess,
  ]);

  const showSkeletons = (tasksLoading && page === 1) || refreshLoading;
  const isInitialEmpty = !tasksLoading && tasks.length === 0 && page === 1;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <VuiBox py={0}>
        <Card>
          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <VuiTypography variant="lg">Maintenance Hub</VuiTypography>

            <VuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <HotelSelector />

              <VuiBox sx={{ width: 220 }}>
                <VuiInput
                  gradient
                  placeholder="Search by name or area..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  // icon={{
                  //   component: "search",
                  //   direction: "left",
                  // }}
                />
              </VuiBox>

              <VuiBox display="flex" gap={1}>
                {can("editMaintenance") && (
                  <VuiButton
                    variant="outlined"
                    color="primary"
                    onClick={() => setBudgetDialogOpen(true)}
                  >
                    <FaWallet size={12} style={{ marginRight: 6 }} />
                    Set Budget
                  </VuiButton>
                )}

                {can("createMaintenance") && (
                  <VuiButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setDialogMode("create");
                      setTaskDialogOpen(true);
                    }}
                  >
                    <FaPlus size={12} style={{ marginRight: 6 }} />
                    Add Task
                  </VuiButton>
                )}
              </VuiBox>
            </VuiBox>
          </VuiBox>

          {/* KPI Cards */}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                title="Open Tasks"
                value={maintenanceCounters?.openTasks || 0}
                icon={<FaTools size={16} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                title="Overdue Tasks"
                value={maintenanceCounters?.overdueTasks || 0}
                icon={<FaExclamationTriangle size={16} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                title="Upcoming"
                value={maintenanceCounters?.upcoming || 0}
                icon={<FaCalendarCheck size={16} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                title="Monthly Budget"
                value={`$${Number(maintenanceCounters?.monthlyBudget || 0).toLocaleString()}`}
                icon={<FaWallet size={16} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                title="Repair Costs"
                value={`$${Number(maintenanceCounters?.repairCost || 0).toLocaleString()}`}
                icon={<FaDollarSign size={16} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={2}>
              <KpiCard
                title="Remaining Budget"
                value={`$${Number(maintenanceCounters?.remainingBudget || 0).toLocaleString()}`}
                icon={<FaWallet size={16} />}
              />
            </Grid>
          </Grid>

          {/* Status & Monthly Filetrs  */}

          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            my={2}
          >
            <VuiBox display="flex" gap={1.5} flexWrap="wrap">
              {["Pending", "Scheduled", "In Progress", "Completed", "All"].map((item) => (
                <VuiButton
                  key={item}
                  size="small"
                  variant={statusFilter === item ? "contained" : "outlined"}
                  color="primary"
                  onClick={() => setStatusFilter(item)}
                >
                  {item}
                </VuiButton>
              ))}
            </VuiBox>

            {/* Right Side Controls */}
            <VuiBox display="flex" alignItems="center" gap={2}>
              <MonthSelector value={monthFilter} onChange={setMonthFilter} />

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, value) => value && setViewMode(value)}
                size="small"
                sx={{
                  background: "rgba(255,255,255,0.04)",
                  borderRadius: "10px",

                  "& .MuiToggleButton-root": {
                    border: "none",
                    color: primary.main,
                    px: 1.5,
                  },
                }}
              >
                <ToggleButton value="table">
                  <FaList size={12} />
                </ToggleButton>

                <ToggleButton value="card">
                  <FaThLarge size={12} />
                </ToggleButton>
              </ToggleButtonGroup>
            </VuiBox>
          </VuiBox>

          {/* Maintenance Grid */}

          {viewMode === "card" ? (
            <>
              <VuiBox
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                }}
                gap={2}
              >
                {showSkeletons ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <CardSkeleton key={i} lines={4} hasHeader hasChips hasFooter />
                  ))
                ) : (
                  <>
                    {tasks?.map((task) => (
                      <MaintenanceCard
                        key={task.id}
                        task={task}
                        onView={handleViewTask}
                        onEdit={handleEditTask}
                        onDelete={(task) => {
                          handleDelete(task);
                        }}
                      />
                    ))}

                    {/* Load More Skeletons */}
                    {loadingMore &&
                      Array.from({ length: 3 }).map((_, index) => (
                        <CardSkeleton
                          key={`loading-${index}`}
                          lines={4}
                          hasHeader
                          hasChips
                          hasFooter
                        />
                      ))}
                  </>
                )}
              </VuiBox>

              {/* No Results */}
              {!tasksLoading && !tasks?.length && (
                <VuiBox
                  py={10}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                >
                  <VuiBox
                    width={70}
                    height={70}
                    mb={2}
                    borderRadius="50%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      background: light.secondary,
                      border: `1px solid ${dark.main}`,
                    }}
                  >
                    <FaTools size={20} sx={{ color: primary.main }} />
                  </VuiBox>

                  <VuiTypography variant="h5" fontWeight="medium" mb={0.5}>
                    No Maintenance Tasks
                  </VuiTypography>

                  <VuiTypography variant="caption" sx={{ maxWidth: 380 }}>
                    No maintenance tasks match the selected filters. Try changing the month, status,
                    or create a new maintenance task.
                  </VuiTypography>
                </VuiBox>
              )}

              {/* Load More */}
              {!showSkeletons && tasks?.length > 0 && hasMore && (
                <VuiBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gap={1}
                  mt={3}
                  onClick={loadMore}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    width: "fit-content",
                    mx: "auto",
                    opacity: 0.9,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      opacity: 1,
                      transform: "translateY(1px)",
                    },
                  }}
                >
                  <VuiTypography
                    variant="caption"
                    sx={{
                      // fontWeight: 500,
                      letterSpacing: "0.3px",
                    }}
                  >
                    Load More
                  </VuiTypography>

                  <FaChevronDown
                    size={12}
                    style={{
                      marginTop: "1px",
                    }}
                  />
                </VuiBox>
              )}
            </>
          ) : (
            <MaintenanceTable
              tasks={tasks || []}
              loading={tasksLoading}
              page={page}
              rowsPerPage={rowsPerPage}
              total={pagination?.total || 0}
              onPageChange={(newPage) => setPage(newPage)}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(value);
                setPage(1);
              }}
              onView={handleViewTask}
              onEdit={handleEditTask}
              onDelete={(task) => {
                handleDelete(task);
              }}
              emptyState={isInitialEmpty}
              emptyTitle="No tasks available"
            />
          )}
        </Card>
      </VuiBox>

      {taskDialogOpen && (
        <MaintenanceTaskDialog
          open={taskDialogOpen}
          mode={dialogMode}
          task={selectedTask}
          loading={dialogMode === "create" ? createTaskLoading : updateTaskLoading}
          error={dialogMode === "create" ? createTaskError : updateTaskError}
          onClose={() => {
            setTaskDialogOpen(false);
            DISPATCH(resetCreateTaskState());
            DISPATCH(resetUpdateTaskState());
          }}
          onSave={(data) => {
            if (dialogMode === "create") {
              handleCreateMaintenanceTask(data);
            } else {
              handleUpdateTask(data);
            }
          }}
        />
      )}

      {detailOpen && (
        <MaintenanceDetailDialog
          open={detailOpen}
          task={selectedTask}
          loading={selectedTaskLoading}
          error={selectedTaskError}
          onClose={() => {
            setDetailOpen(false);
          }}
          onEdit={(task) => {
            setDetailOpen(false);
            handleEditTask(task);
          }}
        />
      )}

      {deleteDialogOpen && (
        <DeleteMaintenanceTaskDialog
          open={deleteDialogOpen}
          task={taskToDelete}
          loading={deleteTaskLoading}
          error={deleteTaskError}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDeleteTask}
        />
      )}

      <MaintenanceBudgetDialog
        open={budgetDialogOpen}
        currentBudget={maintenanceCounters?.monthlyBudget || 0}
        loading={updateBudgetLoading}
        error={updateBudgetError}
        onClose={() => setBudgetDialogOpen(false)}
        onSave={handleSaveBudget}
      />

      <Snackbar
        color={snackbar.color}
        icon={snackbar.icon}
        title={snackbar.title}
        content={snackbar.content}
        open={snackbar.show}
        onClose={closeSnackbar}
        close={closeSnackbar}
      />
    </DashboardLayout>
  );
}

export default MaintenanceHub;
